import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { showToast } from "services/utils/Status";
import { useSelector } from "react-redux";
import { userManagementError } from "services/constants/ErrorMessages";
import { Loader } from "components/Loader";
import SearchIcon from "@mui/icons-material/Search";
import LoginIcon from "@mui/icons-material/Login";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import "../Login/Login.css";
import { tokens, headingSx, secondaryButtonSx } from "../Login/brandTokens";
import {
  AuthLayout,
  AuthLeftPanel,
  AuthRightPanel,
  AuthCard,
  FeatureTimeline,
} from "../Login/AuthShell";
import { parseJsonSafely, responseErrorMessage } from "../Login/authErrors";

const COMPANY_LIST_ERROR = "Couldn't load company list. Please try again.";
const SIGN_IN_ERROR =
  "Couldn't sign in. Please check your details and try again.";
const ACCOUNT_NOT_FOUND_ERROR =
  "We couldn't find an account with those details. Please check and try again.";

const keys = ["domain_name", "firm_name"];

export const ChangeDomain = () => {
  const [tenantDetails, setTenantDetails] = useState([]);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [loading, setLoading] = useState(true);
  const [globalSearchValue, setGlobalSearchValue] = useState("");
  const [searchTenatDetails, setSearchTenatDetails] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");

  useEffect(() => {
    const getTenantDetails = async () => {
      const url = localStorage.getItem("LoginTag")
        ? `${domain}user_tenant/details/?email=${userDetails?.email}`
        : `${domain}user_tenant/details/?phone_number=${userDetails?.phonenumber}`;
      try {
        const userResponse = await fetch(url, {
          method: "GET",
          headers: {
            "content-type": "application/json",
          },
        });
        const resData = await parseJsonSafely(userResponse);
        if (userResponse?.ok) {
          setTenantDetails(resData);
        } else {
          showToast(
            "error",
            responseErrorMessage(userResponse, resData, COMPANY_LIST_ERROR),
          );
        }
      } catch (error) {
        console.error("[ChangeDomain] company list request failed:", error);
        showToast("error", COMPANY_LIST_ERROR);
      } finally {
        // The loader used to stay up forever when this request failed.
        setLoading(false);
      }
    };
    getTenantDetails();
  }, [domain, userDetails?.email, userDetails?.phonenumber]);

  const navigateToDomain = (row) => {
    setSelectedDomain(row.domain_name);
    let domainUrl = "";
    let domainDetails = "";
    if (row.domain_name === "localhost" || row.domain_name === "127.0.0.1") {
      domainUrl = `http://${row.domain_name}:8000/api/`;
      domainDetails = `http://${row.domain_name}:3000`;
    } else {
      domainUrl = `https://${row.domain_name}/api/`;
      domainDetails = `https://${row.domain_name}`;
    }
    localStorage.clear();
    window.setTimeout(() => {
      onSubmit(row, domainUrl, domainDetails);
    }, 120);
  };

  const onSubmit = async (data, domainUrl, domainDetails) => {
    const loginTag = localStorage.getItem("LoginTag");
    setLoading(true);
    try {
    const url =
      loginTag === "Email"
        ? `${domainUrl}login/email_verfication/?email=${data.email}`
        : `${domainUrl}phone_verfication/?phone_no=${data.phonenumber}`;
    const emailResponse = await fetch(url, {
      method: "GET",
      headers: {
        "content-type": "application/json",
      },
    });
    const emailReturnResponse = await parseJsonSafely(emailResponse);
    if (
      emailResponse.status === 200 &&
      emailReturnResponse.user_status === "Active"
    ) {
      localStorage.clear();
      const userId = emailReturnResponse.user_id;
      const userResponse = await fetch(`${domainUrl}custom-token-auth/`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          username: emailReturnResponse.emailuser.username,
          password: data.tenant_password,
        }),
      });
      const userReturnResponse = await parseJsonSafely(userResponse);
      if (userResponse.status !== 200) {
        setLoading(false);
        showToast(
          "error",
          responseErrorMessage(userResponse, userReturnResponse, SIGN_IN_ERROR),
        );
      } else {
        const userData = {
          id: userId,
          first_name: emailReturnResponse.emailuser.first_name,
          username: emailReturnResponse.emailuser.username,
          last_name: emailReturnResponse.emailuser.last_name,
          email: emailReturnResponse.emailuser.email,
          type: emailReturnResponse.type,
          phonenumber: emailReturnResponse.type.phonenumber,
          approver: emailReturnResponse.approver,
          teamlead: emailReturnResponse.teamlead,
          firmName: emailReturnResponse.firm_name,
          approver_id: emailReturnResponse.approver_id,
          image_url: emailReturnResponse.image_url,
          department_id: emailReturnResponse.department_id,
          department_name: emailReturnResponse.department_name,
          employee_id: emailReturnResponse.employee_id,
          designation: emailReturnResponse.designation,
        };
        localStorage.setItem("currentuser", JSON.stringify(userData));
        localStorage.setItem("token", userReturnResponse.token);
        localStorage.setItem("sessionToken", userReturnResponse.session_token);
        localStorage.clear();
        localStorage.setItem("currentScreen", "My Workspace");
        localStorage.setItem("currentSubMenu", "Calendar");
        if (emailReturnResponse.role === "Team Lead") {
          const redirectUrl =
            loginTag === "Email"
              ? `${domainDetails}/admin/teamzone/home?token=${encodeURIComponent(
                  userReturnResponse.token,
                )}&email=${data.email}`
              : `${domainDetails}/admin/teamzone/home?token=${encodeURIComponent(
                  userReturnResponse.token,
                )}&ph_no=${data.phonenumber}`;
          window.location.href = redirectUrl;
        } else {
          const redirectUrl =
            loginTag === "Email"
              ? `${domainDetails}/admin/myzone/status?token=${encodeURIComponent(
                  userReturnResponse.token,
                )}&email=${data.email}`
              : `${domainDetails}/admin/myzone/status?token=${encodeURIComponent(
                  userReturnResponse.token,
                )}&ph_no=${data.phonenumber}`;
          window.location.href = redirectUrl;
        }
      }
    } else if (
      emailReturnResponse.exists === false ||
      emailResponse.status === 400
    ) {
      setLoading(false);
      showToast(
        "error",
        responseErrorMessage(
          emailResponse,
          emailReturnResponse,
          ACCOUNT_NOT_FOUND_ERROR,
        ),
      );
    } else if (emailReturnResponse.user_status === "Inactive") {
      setLoading(false);
      showToast(
        "error",
        responseErrorMessage(
          emailResponse,
          emailReturnResponse,
          userManagementError.emailReturnResponseError,
        ),
      );
    } else {
      // Any other status used to leave the loader spinning with no message.
      setLoading(false);
      showToast(
        "error",
        responseErrorMessage(emailResponse, emailReturnResponse, SIGN_IN_ERROR),
      );
    }
    } catch (error) {
      console.error("[ChangeDomain] workspace sign in failed:", error);
      setLoading(false);
      showToast("error", SIGN_IN_ERROR);
    }
  };

  useEffect(() => {
    const filteredResults = tenantDetails?.filter((item) =>
      keys.some((key) =>
        item[key]
          ?.toString()
          .toLowerCase()
          .includes(globalSearchValue.toString().toLowerCase()),
      ),
    );
    setSearchTenatDetails(filteredResults);
  }, [globalSearchValue, tenantDetails]);

  const handleSignOut = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = "/auth/login";
  };

  const workspaceCount = searchTenatDetails?.length || 0;
  const totalCount = tenantDetails?.length || 0;
  const accentColors = ["#dbf3ef", "#e2efff", "#faedd4", "#fae5ee", "#ece9ff"];
  const accentTextColors = [
    "#2a8a72",
    "#2e6fbe",
    "#9f6510",
    "#aa4568",
    "#5b58d9",
  ];

  const workspaceFeatures = [
    {
      icon: <LoginIcon sx={{ fontSize: 22 }} />,
      title: "Auto-login for single domains",
      subtitle: "If you're linked to only one domain, we take you straight in.",
    },
    {
      icon: <SyncAltIcon sx={{ fontSize: 22 }} />,
      title: "Switch anytime",
      subtitle: "You can switch between your workspaces from within the app.",
    },
    {
      icon: <ShieldOutlinedIcon sx={{ fontSize: 22 }} />,
      title: "Isolated & secure",
      subtitle:
        "Each workspace is fully separate with its own data and permissions.",
    },
  ];

  return loading ? (
    <Loader info="Loading..." />
  ) : (
    <React.Fragment>
      <AuthLayout>
        <AuthLeftPanel
          top={
            <Mui.Stack spacing={4}>
              <Mui.Box sx={{ maxWidth: 520 }}>
                <Mui.Typography
                  sx={{
                    fontSize: { md: 32, lg: 40, xl: 46 },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    letterSpacing: "-0.02em",
                  }}
                >
                  Choose your
                  <br />
                  workspace
                </Mui.Typography>
                <Mui.Typography
                  sx={{
                    mt: 2.5,
                    fontSize: { md: 15, lg: 17 },
                    lineHeight: 1.5,
                    color: "rgba(255,255,255,0.72)",
                    maxWidth: 460,
                  }}
                >
                  You have access to multiple organisations. Select the one
                  you&apos;d like to work in today.
                </Mui.Typography>
              </Mui.Box>

              <FeatureTimeline items={workspaceFeatures} />
            </Mui.Stack>
          }
        />

        <AuthRightPanel>
          <AuthCard
            maxWidth={{ xs: "100%", sm: 560, md: 520, lg: 600, xl: 640 }}
            sx={{
              p: 0,
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 64px)",
              overflow: "hidden",
            }}
          >
            <Mui.Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 4, sm: 4.5 }, pb: 3 }}>
              <Mui.Typography sx={headingSx}>Select a domain</Mui.Typography>
              <Mui.Typography
                sx={{
                  mt: 1.25,
                  fontSize: 15,
                  lineHeight: 1.45,
                  color: "rgba(21,23,26,0.7)",
                }}
              >
                You have access to <b>{totalCount} workspaces.</b> Pick one to
                continue.
              </Mui.Typography>

              <Mui.Box sx={{ mt: 3 }}>
                <Mui.Card
                  component="form"
                  sx={{
                    p: "8px 14px",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    borderRadius: "14px",
                    boxShadow: "none",
                    border: "1px solid rgba(21,23,26,0.16)",
                  }}
                >
                  <SearchIcon sx={{ color: "rgba(21,23,26,0.5)", mr: 1.25 }} />
                  <Mui.InputBase
                    sx={{ flex: 1, fontSize: 15, color: "#15171a" }}
                    placeholder="Search by name or domain..."
                    inputProps={{
                      "aria-label": "Search by name or domain",
                    }}
                    value={globalSearchValue}
                    onChange={(e) => setGlobalSearchValue(e.target.value)}
                  />
                </Mui.Card>
              </Mui.Box>
            </Mui.Box>

            <Mui.Box
              sx={{
                borderTop: "1px solid rgba(21,23,26,0.1)",
                borderBottom: "1px solid rgba(21,23,26,0.1)",
                px: { xs: 3, sm: 4 },
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Mui.Typography
                sx={{
                  fontSize: 13,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  fontWeight: 600,
                  color: "rgba(21,23,26,0.55)",
                }}
              >
                Workspaces
              </Mui.Typography>
              <Mui.Box
                sx={{
                  borderRadius: 999,
                  background: "#f3efe7",
                  px: 1.5,
                  py: 0.5,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "rgba(21,23,26,0.62)",
                  lineHeight: 1,
                }}
              >
                {workspaceCount} OF {totalCount}
              </Mui.Box>
            </Mui.Box>

            <Mui.Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: { xs: 3, sm: 4 },
                py: 2,
              }}
            >
              <Mui.Stack spacing={1.5}>
                {searchTenatDetails?.length === 0 ? (
                  <Mui.Alert severity="info">No data available</Mui.Alert>
                ) : (
                  searchTenatDetails.map((row, index) => (
                    <Mui.Box
                      key={`${row.domain_name}-${index}`}
                      onClick={() => navigateToDomain(row)}
                      sx={{
                        cursor: "pointer",
                        borderRadius: "14px",
                        px: 2,
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        transition: "background-color 0.2s ease",
                        backgroundColor:
                          selectedDomain === row.domain_name
                            ? "#f4faf6"
                            : "transparent",
                        "&:hover": {
                          backgroundColor:
                            selectedDomain === row.domain_name
                              ? "#f4faf6"
                              : "#faf8f4",
                        },
                      }}
                    >
                      <Mui.Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: "12px",
                          background: accentColors[index % accentColors.length],
                          color:
                            accentTextColors[index % accentTextColors.length],
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 15,
                          fontWeight: 600,
                          flexShrink: 0,
                        }}
                      >
                        {row.firm_name
                          ?.split(" ")
                          .filter(Boolean)
                          .slice(0, 2)
                          .map((part) => part.charAt(0).toUpperCase())
                          .join("") || "WS"}
                      </Mui.Box>
                      <Mui.Box sx={{ minWidth: 0, flex: 1 }}>
                        <Mui.Typography
                          sx={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: "#15171a",
                          }}
                          noWrap
                        >
                          {row.firm_name}
                        </Mui.Typography>
                        <Mui.Typography
                          sx={{
                            mt: 0.25,
                            fontSize: 13.5,
                            color: "rgba(21,23,26,0.56)",
                          }}
                          noWrap
                        >
                          {row.domain_name}
                        </Mui.Typography>
                      </Mui.Box>
                      <Mui.Box
                        sx={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          border:
                            selectedDomain === row.domain_name
                              ? `2px solid ${tokens.primary}`
                              : "2px solid rgba(21,23,26,0.22)",
                          background:
                            selectedDomain === row.domain_name
                              ? `radial-gradient(circle, ${tokens.primary} 0 45%, transparent 46%)`
                              : "transparent",
                          flexShrink: 0,
                        }}
                      />
                    </Mui.Box>
                  ))
                )}
              </Mui.Stack>
            </Mui.Box>

            <Mui.Box
              sx={{
                px: { xs: 3, sm: 4 },
                pt: 2.5,
                pb: 3,
                borderTop: "1px solid rgba(21,23,26,0.1)",
              }}
            >
              <Mui.Button
                variant="outlined"
                size="large"
                sx={{ ...secondaryButtonSx, width: "100%" }}
              >
                Select a workspace to continue
              </Mui.Button>
              <Mui.Typography
                sx={{
                  mt: 2,
                  textAlign: "center",
                  fontSize: 13.5,
                  color: "rgba(21,23,26,0.56)",
                  cursor: "pointer",
                }}
                onClick={handleSignOut}
              >
                Not you? Sign out
              </Mui.Typography>
            </Mui.Box>
          </AuthCard>
        </AuthRightPanel>
      </AuthLayout>
    </React.Fragment>
  );
};
